import { useSelector } from 'store';
import useAuth from './useAuth';
import useSendEmailById from './useSendEmailById';
import getNotification from 'utils/getNotificaton';
import notifyCaptureSuppressed from 'utils/notifyCaptureSuppressed';
import { withNotificationLink } from 'utils/notificationLink';
import { AppNotification } from 'types/notification';

export default function useCreateSingleNotification() {
  const { db, user } = useAuth();
  const sendEmailById = useSendEmailById();
  const currentEvent = useSelector((state) => state.currentEvent);
  // Capture switch, read straight from the Redux config mirror (no extra
  // Firestore call). When on, notifications are logged instead of delivered.
  const captureNotifications = useSelector(
    (state) => (state.config.mainConfig as any)?.notificationSettings?.captureNotifications === true
  );

  const createSingleNotification = async (id: string, notificationName: string, context?: { taskTitle?: string }) => {
    const contactRef = db.collection('contacts');
    const message = getNotification(notificationName, currentEvent, context);
    const eventId = currentEvent.mainData?.id;

    const contact = await contactRef
      .doc(id)
      .get()
      .then((doc) => doc.data());
    const isTalentContact = Array.isArray(contact?.accountTypes) && contact.accountTypes.includes('Talent');
    const shouldForceTaskAssignmentEmail = notificationName === 'Assigned To' && isTalentContact;

    const wouldEmail = shouldForceTaskAssignmentEmail || contact?.notificationSubscriptionsEmail?.includes(notificationName);

    // --- CAPTURE ---
    // With config/notificationSettings.captureNotifications on, don't deliver —
    // just log a summary and bail. The same flag gates the server
    // (createServerNotification) so no lane can slip through.
    if (captureNotifications) {
      // eslint-disable-next-line no-console
      console.groupCollapsed(`[CAPTURE] Notification suppressed — not delivered: "${notificationName}"`);
      // eslint-disable-next-line no-console
      console.log('Recipient:', { id, name: contact?.name, email: contact?.email1 });
      // eslint-disable-next-line no-console
      console.log('Subject:', message.subject);
      // eslint-disable-next-line no-console
      console.log('HTML:', message.html);
      // eslint-disable-next-line no-console
      console.log('Would send email:', !!wouldEmail);
      // eslint-disable-next-line no-console
      console.log('In-app fields:', { eventId, link: message.link });
      // eslint-disable-next-line no-console
      console.log(
        'Email HTML (final, per recipient gate):',
        withNotificationLink(message.html.replace(/\n/g, '<br>'), { eventId, link: message.link }, contact)
      );
      // eslint-disable-next-line no-console
      console.groupEnd();
      notifyCaptureSuppressed('Notification');
      return;
    }

    const toAdd: AppNotification = {
      ...message,
      eventId,
      createdBy: user?.name,
      status: 'unread',
      timestamp: new Date()
    };

    try {
      contactRef.doc(id).collection('notifications').add(toAdd);
      if (wouldEmail) {
        const html = withNotificationLink(message.html.replace(/\n/g, '<br>'), { eventId, link: message.link }, contact);
        await sendEmailById({ targetId: id, message: { ...message, html } });
      }
    } catch (error) {
      console.error('Error creating single notification', error);
    }
  };

  return createSingleNotification;
}
